import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiLogOut, FiArrowLeft, FiTrash2, FiEye, FiEyeOff, FiUpload, FiEdit, FiSearch, FiFileText, FiVideo, FiHome, FiBookOpen } from 'react-icons/fi';
import { getMediaUrl } from '../utils/config';

const AdminManage = () => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubcategory, setFilterSubcategory] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('uploadTime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingFile, setEditingFile] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubtitleFile, setEditingSubtitleFile] = useState(null);
  const [showSubtitleEditor, setShowSubtitleEditor] = useState(false);
  const [subtitles, setSubtitles] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [tempSubtitles, setTempSubtitles] = useState([]);
  const [originalSubtitles, setOriginalSubtitles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showReplaceSubtitleModal, setShowReplaceSubtitleModal] = useState(false);
  const [newSubtitleContent, setNewSubtitleContent] = useState('');
  const [newSubtitleFile, setNewSubtitleFile] = useState(null);
  
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  // 检查是否是管理员
  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // 加载上传的文件
  useEffect(() => {
    loadFiles();
  }, []);

  // 当筛选条件变化时重新加载
  useEffect(() => {
    loadFiles();
    // 筛选条件变化时清空选中状态
    setSelectedFiles([]);
  }, [searchTerm, filterCategory, filterSubcategory, startDate, endDate, sortBy, sortOrder]);

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(file => file.id));
    }
  };

  // 单个文件选择
  const toggleSelectFile = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedFiles.length === 0) {
      setMessage('请先选择要删除的视频');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (window.confirm(`确定要删除选中的 ${selectedFiles.length} 个视频吗？这将同时删除关联的字幕文件和词汇数据，此操作不可恢复！`)) {
      try {
        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const fileId of selectedFiles) {
          try {
            const response = await fetch(`/api/videos/${fileId}`, {
              method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error(`删除视频 ${fileId} 失败:`, error);
            failCount++;
          }
        }

        // 清理localStorage中的视频缓存
        const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
        const updatedVideos = uploadedVideos.filter(v => !selectedFiles.includes(v.id));
        localStorage.setItem('uploadedVideos', JSON.stringify(updatedVideos));

        setMessage(`删除完成！成功: ${successCount}, 失败: ${failCount}`);
        setSelectedFiles([]);
        await loadFiles();
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('批量删除失败:', error);
        setMessage('批量删除失败，请重试');
        setTimeout(() => setMessage(''), 3000);
      } finally {
        setLoading(false);
      }
    }
  };

  const loadFiles = async () => {
    try {
      setLoading(true);
      
      // 构建查询参数
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (filterSubcategory && filterSubcategory !== 'all') params.append('subcategory', filterSubcategory);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      
      const response = await fetch(`/api/videos?${params.toString()}`);
      const data = await response.json();
      
      if (data.success && data.videos) {
        // 转换数据格式以匹配前端期望的格式
        const formattedFiles = data.videos.map(video => ({
          id: video.videoId,
          title: video.title,
          description: video.description || '',
          category: video.category,
          subcategory: video.subcategory || '',
          level: video.level || 'Starters',
          duration: video.duration || 0,
          fileUrl: video.videoUrl.startsWith('http') ? video.videoUrl : `${window.location.origin}${video.videoUrl}`,
          subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
            ? (video.subtitleFiles[0].startsWith('http') 
                ? video.subtitleFiles[0] 
                : `${window.location.origin}${video.subtitleFiles[0].startsWith('/') ? '' : '/'}${video.subtitleFiles[0]}`)
            : '',
          posterUrl: video.posterUrl 
            ? (video.posterUrl.startsWith('http') 
                ? video.posterUrl 
                : `${window.location.origin}${video.posterUrl}`)
            : '',
          fileType: 'video',
          hidden: false,
          uploadedAt: video.uploadTime,
          subtitleFiles: video.subtitleFiles || []
        }));
        setFiles(formattedFiles);
      } else {
        setFiles([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading files:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (window.confirm('确定要删除这个视频吗？这将同时删除关联的字幕文件和词汇数据，此操作不可恢复！')) {
      try {
        setLoading(true);
        const response = await fetch(`/api/videos/${fileId}`, {
          method: 'DELETE'
        });
        
        const data = await response.json();
        
        if (data.success) {
          setMessage('删除成功！');
          // 清理localStorage中的视频缓存
          const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
          const updatedVideos = uploadedVideos.filter(v => v.id !== fileId);
          localStorage.setItem('uploadedVideos', JSON.stringify(updatedVideos));
          // 重新加载数据
          await loadFiles();
        } else {
          setMessage(data.error || '删除失败，请重试');
        }
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting file:', error);
        setMessage('删除失败，请重试');
        setTimeout(() => setMessage(''), 3000);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleVisibility = (fileId) => {
    try {
      const uploadedFiles = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
      const updatedFiles = uploadedFiles.map(file => {
        if (file.id === fileId) {
          return { ...file, hidden: !file.hidden };
        }
        return file;
      });
      localStorage.setItem('uploadedVideos', JSON.stringify(updatedFiles));
      setFiles(updatedFiles);
      setMessage('设置成功！');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      setMessage('设置失败，请重试');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleEdit = (file) => {
    setEditingFile(file);
    setEditForm({
      title: file.title,
      description: file.description,
      category: file.category,
      subcategory: file.subcategory || '',
      level: file.level,
      duration: Math.floor(file.duration / 60) // 显示为分钟数
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // 调用后端API更新数据库
      const response = await fetch(`/api/videos/${editingFile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description,
          category: editForm.category,
          subcategory: editForm.subcategory,
          level: editForm.level
        }),
      });

      if (!response.ok) {
        throw new Error('更新失败');
      }

      const data = await response.json();
      console.log('更新成功:', data);
      
      setMessage('编辑成功！数据库已更新');
      setTimeout(() => setMessage(''), 3000);
      setShowEditModal(false);
      setEditingFile(null);
      
      // 重新从数据库加载最新数据
      await loadFiles();
    } catch (error) {
      console.error('Error editing file:', error);
      setMessage('编辑失败，请重试');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      universe: '宇宙与空间科学',
      physics: '物理学',
      chemistry: '化学',
      earth: '地球科学',
      biology: '基础生物学',
      plants: '植物学',
      animals: '动物学',
      microbes: '微生物学',
      ecology: '生态学',
      human: '人体科学'
    };
    return categoryMap[category] || '科学';
  };

  const getLevelLabel = (level) => {
    const levelMap = {
      'Starters': 'Starters',
      'Movers': 'Movers',
      'Flyers': 'Flyers',
      'KET': 'KET',
      'PET': 'PET',
      'FCE': 'FCE',
      'beginner': 'Starters',
      'intermediate': 'Movers',
      'advanced': 'Flyers',
      'ket': 'KET',
      'pet': 'PET',
      'fce': 'FCE'
    };
    return levelMap[level] || level || '未设置';
  };

  // 解析SRT字幕文件
  const parseSRT = (srtContent) => {
    const subtitles = [];
    const blocks = srtContent.trim().split(/\n\s*\n/);
    
    blocks.forEach(block => {
      const lines = block.split('\n');
      if (lines.length >= 3) {
        const timeLine = lines[1];
        const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
        
        if (timeMatch) {
          const startHours = parseInt(timeMatch[1]);
          const startMinutes = parseInt(timeMatch[2]);
          const startSeconds = parseInt(timeMatch[3]);
          const startMs = parseInt(timeMatch[4]);
          const startTime = startHours * 3600 + startMinutes * 60 + startSeconds + startMs / 1000;
          
          const textLines = lines.slice(2);
          let english = '';
          let chinese = '';
          
          textLines.forEach(line => {
            if (/[\u4e00-\u9fa5]/.test(line)) {
              chinese += (chinese ? ' ' : '') + line;
            } else if (line.trim()) {
              english += (english ? ' ' : '') + line;
            }
          });
          
          subtitles.push({
            time: startTime,
            english: english.trim(),
            chinese: chinese.trim()
          });
        }
      }
    });
    
    return subtitles;
  };

  // 解析VTT字幕文件
  const parseVTT = (vttContent) => {
    const subtitles = [];
    const lines = vttContent.trim().split('\n');
    let i = 0;
    
    while (i < lines.length && !lines[i].includes('-->')) {
      i++;
    }
    
    while (i < lines.length) {
      const timeLine = lines[i];
      const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
      
      if (!timeMatch) {
        const shortTimeMatch = timeLine.match(/(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2})\.(\d{3})/);
        if (shortTimeMatch) {
          const startMinutes = parseInt(shortTimeMatch[1]);
          const startSeconds = parseInt(shortTimeMatch[2]);
          const startMs = parseInt(shortTimeMatch[3]);
          const startTime = startMinutes * 60 + startSeconds + startMs / 1000;
          
          i++;
          let english = '';
          let chinese = '';
          
          while (i < lines.length && lines[i].trim() !== '') {
            const line = lines[i];
            if (/[\u4e00-\u9fa5]/.test(line)) {
              chinese += (chinese ? ' ' : '') + line;
            } else if (line.trim()) {
              english += (english ? ' ' : '') + line;
            }
            i++;
          }
          
          subtitles.push({
            time: startTime,
            english: english.trim(),
            chinese: chinese.trim()
          });
        }
        i++;
        continue;
      }
      
      const startHours = parseInt(timeMatch[1]);
      const startMinutes = parseInt(timeMatch[2]);
      const startSeconds = parseInt(timeMatch[3]);
      const startMs = parseInt(timeMatch[4]);
      const startTime = startHours * 3600 + startMinutes * 60 + startSeconds + startMs / 1000;
      
      i++;
      let english = '';
      let chinese = '';
      
      while (i < lines.length && lines[i].trim() !== '') {
        const line = lines[i];
        if (/[\u4e00-\u9fa5]/.test(line)) {
          chinese += (chinese ? ' ' : '') + line;
        } else if (line.trim()) {
          english += (english ? ' ' : '') + line;
        }
        i++;
      }
      
      subtitles.push({
        time: startTime,
        english: english.trim(),
        chinese: chinese.trim()
      });
      
      while (i < lines.length && lines[i].trim() === '') {
        i++;
      }
    }
    
    return subtitles;
  };

  // 从字幕文件URL加载字幕
  const loadSubtitlesFromFile = async (subtitleUrl) => {
    try {
      console.log('从字幕文件加载:', subtitleUrl);
      
      // 生成多种可能的URL格式
      const possibleUrls = [];
      
      // 原始URL
      possibleUrls.push(subtitleUrl);
      
      // 如果不是完整URL，尝试添加相对路径
      if (!subtitleUrl.startsWith('http')) {
        if (!subtitleUrl.startsWith('/')) {
          possibleUrls.push(`/${subtitleUrl}`);
        }
        // 使用环境变量配置的服务器路径
        const serverUrl = getMediaUrl('');
        if (serverUrl) {
          possibleUrls.push(`${serverUrl}${subtitleUrl.startsWith('/') ? '' : '/'}${subtitleUrl}`);
        }
        // 尝试添加完整的本地开发服务器路径
        possibleUrls.push(`${window.location.origin}${subtitleUrl.startsWith('/') ? '' : '/'}${subtitleUrl}`);
      }
      
      let content = null;
      let finalUrl = null;
      
      // 尝试所有可能的URL格式
      for (const url of possibleUrls) {
        let encodedUrl = url;
        if (url.startsWith('http')) {
          try {
            const urlObj = new URL(url);
            if (!urlObj.pathname.includes('%20')) {
              urlObj.pathname = encodeURI(urlObj.pathname);
            }
            encodedUrl = urlObj.toString();
          } catch (e) {
            encodedUrl = url;
          }
        } else {
          if (!url.includes('%20')) {
            encodedUrl = encodeURI(url);
          }
        }
        
        console.log('尝试加载字幕URL:', encodedUrl);
        try {
          const response = await fetch(encodedUrl, {
            mode: 'cors',
            headers: {
              'Accept': 'text/plain, text/srt, text/vtt'
            }
          });
          
          if (response.ok) {
            content = await response.text();
            finalUrl = encodedUrl;
            console.log('字幕文件加载成功:', encodedUrl);
            console.log('字幕文件内容长度:', content.length);
            break;
          } else {
            console.log('字幕文件加载失败，状态码:', response.status, 'URL:', encodedUrl);
          }
        } catch (error) {
          console.log('加载字幕文件时出错:', error, 'URL:', encodedUrl);
        }
      }
      
      if (!content) {
        console.log('所有URL格式都加载失败');
        return [];
      }
      
      let parsedSubtitles = [];
      
      if (finalUrl.toLowerCase().endsWith('.srt')) {
        parsedSubtitles = parseSRT(content);
        console.log('SRT字幕解析完成，数量:', parsedSubtitles.length);
      } else if (finalUrl.toLowerCase().endsWith('.vtt')) {
        parsedSubtitles = parseVTT(content);
        console.log('VTT字幕解析完成，数量:', parsedSubtitles.length);
      } else {
        console.log('未知字幕格式，尝试两种格式解析');
        parsedSubtitles = parseSRT(content);
        if (parsedSubtitles.length === 0) {
          parsedSubtitles = parseVTT(content);
        }
        console.log('字幕解析完成，数量:', parsedSubtitles.length);
      }
      
      if (parsedSubtitles.length > 0) {
        return parsedSubtitles;
      } else {
        return [];
      }
    } catch (error) {
      console.error('加载字幕文件失败:', error);
      return [];
    }
  };

  // 进入编辑模式
  const enterEditMode = () => {
    setOriginalSubtitles([...subtitles]);
    setTempSubtitles([...subtitles]);
    setEditMode(true);
    setEditingIndex(null);
    console.log('进入编辑模式');
  };

  // 退出编辑模式
  const exitEditMode = async (save = false) => {
    if (save) {
      setSubtitles([...tempSubtitles]);
      console.log('保存字幕修改');
      
      // 保存字幕到服务器
      if (editingSubtitleFile && editingSubtitleFile.id) {
        try {
          const response = await fetch('/api/subtitles/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              videoId: editingSubtitleFile.id,
              subtitles: tempSubtitles
            })
          });
          
          const data = await response.json();
          if (data.success) {
            alert('字幕保存成功！');
            console.log('字幕已保存到服务器:', data.subtitleUrl);
          } else {
            alert('字幕保存失败: ' + (data.error || '未知错误'));
          }
        } catch (error) {
          console.error('保存字幕失败:', error);
          alert('字幕保存失败，请重试');
        }
      }
    } else {
      setSubtitles([...originalSubtitles]);
      console.log('撤销字幕修改');
    }
    setEditMode(false);
    setEditingIndex(null);
  };

  // 开始编辑某个字幕
  const startEditSubtitle = (index) => {
    setEditingIndex(index);
  };

  // 保存单个字幕的编辑
  const saveSubtitleEdit = () => {
    setEditingIndex(null);
  };

  // 更新字幕时间
  const updateSubtitleTime = (index, newTime) => {
    const newSubtitles = [...tempSubtitles];
    newSubtitles[index] = { ...newSubtitles[index], time: parseFloat(newTime) };
    setTempSubtitles(newSubtitles);
  };

  // 更新字幕英文文本
  const updateSubtitleEnglish = (index, newText) => {
    const newSubtitles = [...tempSubtitles];
    newSubtitles[index] = { ...newSubtitles[index], english: newText };
    setTempSubtitles(newSubtitles);
  };

  // 更新字幕中文文本
  const updateSubtitleChinese = (index, newText) => {
    const newSubtitles = [...tempSubtitles];
    newSubtitles[index] = { ...newSubtitles[index], chinese: newText };
    setTempSubtitles(newSubtitles);
  };

  // 添加新字幕
  const addNewSubtitle = () => {
    const newTime = tempSubtitles.length > 0 
      ? tempSubtitles[tempSubtitles.length - 1].time + 3 
      : 0;
    const newSubtitles = [...tempSubtitles, {
      time: newTime,
      english: 'New subtitle',
      chinese: '新字幕'
    }];
    setTempSubtitles(newSubtitles);
  };

  // 删除字幕
  const deleteSubtitle = (index) => {
    if (tempSubtitles.length <= 1) {
      alert('至少需要保留一条字幕');
      return;
    }
    const newSubtitles = tempSubtitles.filter((_, i) => i !== index);
    setTempSubtitles(newSubtitles);
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  // 打开字幕编辑器
  const handleEditSubtitles = async (file) => {
    setEditingSubtitleFile(file);
    if (file.subtitleUrl) {
      const loadedSubtitles = await loadSubtitlesFromFile(file.subtitleUrl);
      setSubtitles(loadedSubtitles);
    } else {
      setSubtitles([]);
    }
    setShowSubtitleEditor(true);
  };

  // 打开词汇管理页面，直接跳转到该视频的词汇管理
  const handleManageVocabulary = (file) => {
    navigate(`/admin/vocabulary?videoId=${file.id}`);
  };



  const categories = [
    { value: 'universe', label: '宇宙与空间科学' },
    { value: 'physics', label: '物理学' },
    { value: 'chemistry', label: '化学' },
    { value: 'earth', label: '地球科学' },
    { value: 'biology', label: '基础生物学' },
    { value: 'plants', label: '植物学' },
    { value: 'animals', label: '动物学' },
    { value: 'microbes', label: '微生物学' },
    { value: 'ecology', label: '生态学' },
    { value: 'human', label: '人体科学' }
  ];

  const levels = [
    { value: 'Starters', label: 'Starters' },
    { value: 'Movers', label: 'Movers' },
    { value: 'Flyers', label: 'Flyers' },
    { value: 'KET', label: 'KET' },
    { value: 'PET', label: 'PET' },
    { value: 'FCE', label: 'FCE' }
  ];

  // 各分类的详细子分类
  const subcategories = {
    universe: [
      { value: 'stars_galaxies_blackholes', label: '恒星、星系与黑洞 - Stars, Galaxies & Black Holes' },
      { value: 'solar_system', label: '太阳系 - The Solar System' },
      { value: 'moon_space_exploration', label: '月球与太空探索 - The Moon & Space Exploration' },
      { value: 'astronomy_observation', label: '天文观测 - Astronomy & Sky Watching' }
    ],
    physics: [
      { value: 'forces_motion', label: '力与运动 - Forces & Motion' },
      { value: 'energy', label: '能量 - Energy（含热、电、简单电路）' },
      { value: 'sound_light', label: '声与光 - Sound & Light' },
      { value: 'magnetism', label: '磁 - Magnetism' }
    ],
    chemistry: [
      { value: 'states_properties', label: '物质的状态与性质 - States & Properties of Matter' },
      { value: 'mixtures_separation', label: '混合物与分离 - Mixtures & Separation' },
      { value: 'acids_bases_reactions', label: '酸碱与反应 - Acids, Bases & Reactions' },
      { value: 'elements_periodic_table', label: '元素与周期表 - Elements & The Periodic Table' },
      { value: 'everyday_chemistry', label: '生活中的化学 - Chemistry in Everyday Life' }
    ],
    earth: [
      { value: 'rocks_fossils_geology', label: '岩石、化石与地质 - Rocks, Fossils & Geology' },
      { value: 'water_cycle_oceans', label: '水循环与海洋 - Water Cycle & Oceans' },
      { value: 'weather_climate', label: '天气与气候 - Weather & Climate' },
      { value: 'maps_globes', label: '地图与地球仪 - Maps & Globes' },
      { value: 'soil_land', label: '土壤与陆地 - Soil & Land' }
    ],
    biology: [
      { value: 'cells_genetics', label: '细胞与遗传 - Cells & Genetics' },
      { value: 'life_processes', label: '生命过程 - Life Processes（生命周期、进化）' },
      { value: 'ecology', label: '生态关系 - Ecology（食物链、栖息地）' },
      { value: 'classification_adaptations', label: '生物分类与适应 - Classification & Adaptations' },
      { value: 'humans_conservation', label: '人类与自然保护 - Humans & Conservation' }
    ],
    plants: [
      { value: 'seeds_growth', label: '种子与生长 - Seeds & Growth' },
      { value: 'flowers_pollination', label: '花与授粉 - Flowers & Pollination' },
      { value: 'photosynthesis_nutrition', label: '光合作用与养分 - Photosynthesis & Nutrition' },
      { value: 'plant_defenses_carnivorous', label: '植物的防御与奇特植物 - Plant Defenses & Carnivorous Plants' },
      { value: 'fungi_useful_plants', label: '真菌与人类使用的植物 - Fungi & Useful Plants' }
    ],
    animals: [
      { value: 'animal_classification', label: '动物分类 - Animal Classification（哺乳动物、鸟类、爬行类、两栖类、鱼类、无脊椎动物）' },
      { value: 'insects_spiders', label: '昆虫与蜘蛛 - Insects & Spiders' },
      { value: 'ocean_animals', label: '海洋动物 - Ocean Animals' },
      { value: 'animal_behaviors_life_cycles', label: '动物行为与生命周期 - Animal Behaviors & Life Cycles' },
      { value: 'endangered_animals_conservation', label: '濒危动物保护 - Endangered Animals & Conservation' }
    ],
    microbes: [
      { value: 'types_of_microbes', label: '微生物的种类 - Types of Microbes（细菌、病毒、真菌、原生生物）' },
      { value: 'habitats_of_microbes', label: '微生物的栖息地 - Habitats of Microbes' },
      { value: 'helpful_harmful_microbes', label: '有益与有害微生物 - Helpful & Harmful Microbes' },
      { value: 'microbes_food', label: '微生物与食物 - Microbes & Food' },
      { value: 'microscopes_observation', label: '显微镜与观察 - Microscopes & Observation' }
    ],
    ecology: [
      { value: 'land_ecosystems', label: '陆地生态 - Land Ecosystems（森林、沙漠、草原、极地）' },
      { value: 'water_ecosystems', label: '水域生态 - Water Ecosystems（海洋、淡水）' },
      { value: 'urban_ecology', label: '城市生态 - Urban Ecology' },
      { value: 'conservation', label: '生态保护 - Conservation（回收、生物多样性、可持续发展）' },
      { value: 'ecological_processes', label: '生态过程 - Ecological Processes（食物网、人类影响）' }
    ],
    human: [
      { value: 'skeletal_muscular_system', label: '骨骼与肌肉系统 - Skeletal & Muscular System' },
      { value: 'circulatory_respiratory_system', label: '循环与呼吸系统 - Circulatory & Respiratory System' },
      { value: 'digestive_endocrine_system', label: '消化与内分泌 - Digestive & Endocrine System' },
      { value: 'senses_nervous_system', label: '感官与神经系统 - Senses & Nervous System' },
      { value: 'health_disease', label: '健康与疾病 - Health & Disease' }
    ]
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c' }}>
      {/* 顶部导航栏 */}
      <nav style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        padding: '1rem 0',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="container" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            textDecoration: 'none',
            color: '#1a2b4c'
          }}>
            <img 
              src="/logo.png" 
              alt="123wonder英文科普" 
              style={{ 
                height: '50px', 
                width: 'auto'
              }} 
            />
            <div>
              <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c' }}>管理员后台</h1>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#6c757d' }}>文件管理</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button 
              onClick={() => navigate('/admin/upload')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              上传文件
            </button>
            <button 
              onClick={() => navigate('/admin/vocabulary')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              词汇管理
            </button>
            <button 
              onClick={() => navigate('/admin/invitation')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              邀请码管理
            </button>
            <button 
              onClick={() => navigate('/admin/users')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              用户管理
            </button>
            <span style={{ color: '#495057', fontSize: '1.1rem' }}>欢迎，{user?.username}</span>
            <button 
              onClick={handleLogout}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.25rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                fontSize: '1.1rem'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <FiLogOut /> 退出
            </button>
          </div>
        </div>
      </nav>

      <div style={{ padding: '2rem 0', backgroundColor: '#f8f9fa' }}>
        <div className="container" style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <button 
              onClick={() => navigate('/admin/upload')}
              style={{
                background: '#1976d2',
                border: 'none',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
            >
              <FiUpload /> 上传新文件
            </button>
            <button 
              onClick={() => navigate('/')}
              style={{
                background: 'none',
                border: '1px solid #e9ecef',
                color: '#1a2b4c',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <FiHome /> 返回首页
            </button>
          </div>

          {message && (
            <div style={{ 
              backgroundColor: message.includes('成功') ? '#d4edda' : '#f8d7da',
              color: message.includes('成功') ? '#155724' : '#721c24',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: message.includes('成功') ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
            }}>
              {message}
            </div>
          )}

          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 style={{ 
              marginBottom: '2rem',
              color: '#1a2b4c',
              fontSize: '1.8rem',
              fontWeight: '700'
            }}>文件管理</h2>

            {/* 搜索和筛选 */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginBottom: '2rem',
              flexWrap: 'wrap',
              alignItems: 'flex-end'
            }}>
              <div style={{ flex: 1, minWidth: '250px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>搜索标题</label>
                <div style={{ 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FiSearch style={{ 
                    position: 'absolute', 
                    left: '1rem', 
                    color: '#6c757d'
                  }} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="搜索视频标题"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem 0.75rem 3rem',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>分类</label>
                <select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setFilterSubcategory('all'); // 重置子分类
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="all">所有分类</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              {/* 子分类筛选 */}
              {filterCategory !== 'all' && subcategories[filterCategory] && (
                <div style={{ minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>子分类</label>
                  <select
                    value={filterSubcategory}
                    onChange={(e) => setFilterSubcategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="all">所有子分类</option>
                    {subcategories[filterCategory].map(sub => (
                      <option key={sub.value} value={sub.value}>{sub.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>开始日期</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>
              
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>结束日期</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>
              
              <div style={{ minWidth: '140px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>排序字段</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="uploadTime">上传时间</option>
                  <option value="title">标题</option>
                  <option value="duration">时长</option>
                </select>
              </div>
              
              <div style={{ minWidth: '120px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#495057', fontWeight: '500' }}>排序方式</label>
                <button
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {sortOrder === 'desc' ? '↓ 降序' : '↑ 升序'}
                </button>
              </div>
              
              <div style={{ minWidth: '120px', alignSelf: 'flex-end' }}>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('all');
                    setStartDate('');
                    setEndDate('');
                    setSortBy('uploadTime');
                    setSortOrder('desc');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: '#6c757d',
                    border: 'none',
                    color: 'white',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  重置
                </button>
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div style={{ 
                marginBottom: '1rem', 
                padding: '1rem', 
                backgroundColor: '#fff3cd', 
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#856404', fontWeight: '500' }}>
                  已选择 {selectedFiles.length} 个视频
                </span>
                <button
                  onClick={handleBatchDelete}
                  style={{
                    background: '#dc3545',
                    border: 'none',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                >
                  <FiTrash2 /> 批量删除
                </button>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '4rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
                <p style={{ color: '#6c757d' }}>加载中...</p>
              </div>
            ) : files.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📁</div>
                <p style={{ color: '#6c757d' }}>暂无上传的文件</p>
                <button 
                  onClick={() => navigate('/admin/upload')}
                  style={{
                    marginTop: '1rem',
                    background: '#1976d2',
                    border: 'none',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                >
                  上传文件
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        color: '#495057',
                        fontWeight: '600',
                        width: '60px'
                      }}>编号</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        color: '#495057',
                        fontWeight: '600',
                        width: '60px'
                      }}>
                        <input
                          type="checkbox"
                          checked={files.length > 0 && selectedFiles.length === files.length}
                          onChange={toggleSelectAll}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>标题</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>分类</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>详细分类</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>难度</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>时长</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>字幕文件</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>上传时间</th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        color: '#495057',
                        fontWeight: '600'
                      }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file, index) => (
                      <tr key={file.id} style={{ 
                        borderBottom: '1px solid #e9ecef',
                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white'
                      }}>
                        <td style={{ padding: '1rem', textAlign: 'center', color: '#6c757d', fontWeight: '600' }}>
                          {files.length - index}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedFiles.includes(file.id)}
                            onChange={() => toggleSelectFile(file.id)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                        </td>
                        <td style={{ padding: '1rem', color: '#1a2b4c' }}>{file.title}</td>
                        <td style={{ padding: '1rem', color: '#6c757d' }}>{getCategoryLabel(file.category)}</td>
                        <td style={{ padding: '1rem', color: '#6c757d' }}>{file.subcategory ? (subcategories[file.category] || []).find(subcat => subcat.value === file.subcategory)?.label || file.subcategory : '-'}</td>
                        <td style={{ padding: '1rem', color: '#6c757d' }}>{getLevelLabel(file.level)}</td>
                        <td style={{ padding: '1rem', color: '#6c757d' }}>{Math.floor(file.duration / 60)}:{(file.duration % 60).toString().padStart(2, '0')}</td>
                        <td style={{ 
                          padding: '1rem', 
                          color: '#6c757d',
                          maxWidth: '200px',
                          wordWrap: 'break-word',
                          wordBreak: 'break-all'
                        }}>
                          {file.subtitleFiles && file.subtitleFiles.length > 0 ? (
                            <div>
                              {file.subtitleFiles.map((sub, idx) => (
                                <div key={idx} style={{ 
                                  fontSize: '0.85rem', 
                                  marginBottom: idx < file.subtitleFiles.length - 1 ? '0.25rem' : 0,
                                  wordWrap: 'break-word',
                                  wordBreak: 'break-all'
                                }}>
                                  • {sub.split('/').pop()}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#adb5bd' }}>无</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', color: '#6c757d', fontSize: '0.9rem' }}>
                          {new Date(file.uploadedAt).toLocaleString('zh-CN')}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button 
                              onClick={() => handleEdit(file)}
                              style={{
                                background: 'none',
                                border: '1px solid #b3d9ff',
                                color: '#0066ff',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease'
                              }}
                              title="编辑视频信息"
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e6f3ff'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <FiEdit />
                            </button>
                            {file.subtitleFiles && file.subtitleFiles.length > 0 && (
                              <button 
                                onClick={() => handleEditSubtitles(file)}
                                style={{
                                  background: 'none',
                                  border: '1px solid #e9ecef',
                                  color: '#1a2b4c',
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease'
                                }}
                                title="编辑字幕"
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <FiFileText />
                              </button>
                            )}
                            <button 
                              onClick={() => handleManageVocabulary(file)}
                              style={{
                                background: 'none',
                                border: '1px solid #e9ecef',
                                color: '#1a2b4c',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease'
                              }}
                              title="词汇管理"
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <FiBookOpen />
                            </button>
                            <button 
                              onClick={() => handleDelete(file.id)}
                              style={{
                                background: 'none',
                                border: '1px solid #f5c6cb',
                                color: '#721c24',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease'
                              }}
                              title="删除（级联删除字幕）"
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8d7da'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 编辑模态框 */}
      {showEditModal && editingFile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              marginBottom: '1.5rem',
              color: '#1a2b4c',
              fontSize: '1.5rem',
              fontWeight: '700'
            }}>编辑文件信息</h3>
            <form onSubmit={handleEditSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>标题</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>分类</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value, subcategory: '' })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>详细分类</label>
                  <select
                    value={editForm.subcategory}
                    onChange={(e) => setEditForm({ ...editForm, subcategory: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">请选择详细分类</option>
                    {(subcategories[editForm.category] || []).map(subcat => (
                      <option key={subcat.value} value={subcat.value}>{subcat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>难度</label>
                  <select
                    value={editForm.level}
                    onChange={(e) => setEditForm({ ...editForm, level: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {levels.map(lev => (
                      <option key={lev.value} value={lev.value}>{lev.label}</option>
                    ))}
                  </select>
                </div>
                {editingFile.fileType === 'video' && (
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: '#495057',
                      fontWeight: '500'
                    }}>时长（分钟）</label>
                    <input
                      type="number"
                      value={editForm.duration}
                      onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                      min="1"
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        color: '#495057',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500'
                }}>描述</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    resize: 'vertical',
                    transition: 'all 0.3s ease'
                  }}
                ></textarea>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingFile(null);
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #e9ecef',
                    color: '#1a2b4c',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  取消
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#1976d2',
                    border: 'none',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                >
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 字幕编辑器模态框 */}
      {showSubtitleEditor && editingSubtitleFile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '95%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{
                margin: 0,
                color: '#1a2b4c',
                fontSize: '1.5rem',
                fontWeight: '700'
              }}>编辑字幕 - {editingSubtitleFile.title}</h3>
              <button
                onClick={() => {
                  setShowSubtitleEditor(false);
                  setEditingSubtitleFile(null);
                  setEditMode(false);
                  setEditingIndex(null);
                }}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                关闭
              </button>
            </div>

            {/* 字幕编辑器工具栏 */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {!editMode ? (
                <button
                  onClick={enterEditMode}
                  style={{
                    background: '#1976d2',
                    border: 'none',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                >
                  进入编辑模式
                </button>
              ) : (
                <>
                  <button
                    onClick={() => addNewSubtitle()}
                    style={{
                      background: '#28a745',
                      border: 'none',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                  >
                    + 添加字幕
                  </button>
                  <button
                    onClick={() => exitEditMode(true)}
                    style={{
                      background: '#28a745',
                      border: 'none',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                  >
                    保存修改
                  </button>
                  <button
                    onClick={() => exitEditMode(false)}
                    style={{
                      background: '#dc3545',
                      border: 'none',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                  >
                    撤销修改
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setShowReplaceSubtitleModal(true);
                  setNewSubtitleContent('');
                  setNewSubtitleFile(null);
                }}
                style={{
                  background: '#ffc107',
                  border: 'none',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e0a800'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffc107'}
              >
                重新添加整篇字幕
              </button>
            </div>

            {/* 字幕列表 */}
            <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '1rem' }}>
              {((editMode ? tempSubtitles : subtitles).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  <p>暂无字幕，请先添加字幕</p>
                </div>
              ) : (
                (editMode ? tempSubtitles : subtitles).map((subtitle, index) => {
                  const isEditing = editMode && editingIndex === index;
                  const displaySubtitles = editMode ? tempSubtitles : subtitles;
                  
                  if (isEditing) {
                    return (
                      <div key={index} style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(40, 167, 69, 0.1)',
                        border: '2px solid #28a745'
                      }}>
                        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', marginBottom: '0.25rem' }}>时间（秒）</label>
                            <input
                              type="number"
                              step="0.1"
                              value={displaySubtitles[index].time}
                              onChange={(e) => updateSubtitleTime(index, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #ced4da',
                                fontSize: '0.9rem'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={saveSubtitleEdit}
                              style={{
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#28a745',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '500'
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => deleteSubtitle(index)}
                              style={{
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '500'
                              }}
                              title="删除字幕"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', marginBottom: '0.25rem' }}>英文</label>
                          <textarea
                            value={displaySubtitles[index].english}
                            onChange={(e) => updateSubtitleEnglish(index, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              border: '1px solid #ced4da',
                              fontSize: '0.95rem',
                              minHeight: '60px',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', marginBottom: '0.25rem' }}>中文</label>
                          <textarea
                            value={displaySubtitles[index].chinese}
                            onChange={(e) => updateSubtitleChinese(index, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              border: '1px solid #ced4da',
                              fontSize: '0.95rem',
                              minHeight: '60px',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={index} style={{
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(240,240,240,0.5)',
                      border: '1px solid #e0e0e0',
                      cursor: editMode ? 'pointer' : 'default'
                    }} 
                    onClick={() => editMode && startEditSubtitle(index)}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)' }}>
                          {Math.floor(subtitle.time / 60).toString().padStart(2, '0')}:{(subtitle.time % 60).toString().padStart(2, '0')}
                        </div>
                        {editMode && (
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditSubtitle(index);
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #1a73e8',
                                backgroundColor: 'rgba(26, 115, 232, 0.1)',
                                color: '#1a73e8',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              编辑
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSubtitle(index);
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #dc3545',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              删除
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div style={{ color: '#333333', marginBottom: '0.3rem', lineHeight: '1.4' }}>
                        {subtitle.english}
                      </div>
                      {subtitle.chinese && (
                        <div style={{ color: 'rgba(0,0,0,0.6)', lineHeight: '1.4', fontSize: '0.9rem' }}>
                          {subtitle.chinese}
                        </div>
                      )}
                    </div>
                  );
                })
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 重新添加整篇字幕模态框 */}
      {showReplaceSubtitleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{
                margin: 0,
                color: '#1a2b4c',
                fontSize: '1.5rem',
                fontWeight: '700'
              }}>重新添加整篇字幕</h3>
              <button
                onClick={() => setShowReplaceSubtitleModal(false)}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                关闭
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                color: '#495057',
                fontWeight: '500'
              }}>
                方式一：上传字幕文件
              </label>
              <input
                type="file"
                accept=".srt,.vtt,.txt"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setNewSubtitleFile(file);
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setNewSubtitleContent(event.target.result);
                    };
                    reader.readAsText(file);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  border: '1px solid #e9ecef',
                  color: '#495057',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease'
                }}
              />
              {newSubtitleFile && (
                <div style={{ marginTop: '0.5rem', color: '#28a745' }}>
                  已选择文件: {newSubtitleFile.name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '0.5rem',
                color: '#495057',
                fontWeight: '500'
              }}>
                方式二：粘贴字幕内容
              </label>
              <textarea
                value={newSubtitleContent}
                onChange={(e) => setNewSubtitleContent(e.target.value)}
                placeholder="请粘贴 SRT 或 VTT 格式的字幕内容..."
                rows={12}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  border: '1px solid #e9ecef',
                  color: '#495057',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057', fontSize: '1rem' }}>
                说明
              </h4>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#6c757d' }}>
                <li style={{ marginBottom: '0.5rem' }}>支持 SRT 和 VTT 格式的字幕文件</li>
                <li style={{ marginBottom: '0.5rem' }}>可以通过上传文件或粘贴内容两种方式添加字幕</li>
                <li style={{ marginBottom: '0.5rem' }}>替换后会覆盖原有的所有字幕</li>
                <li>建议先备份原有字幕</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowReplaceSubtitleModal(false);
                  setNewSubtitleContent('');
                  setNewSubtitleFile(null);
                }}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                取消
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!newSubtitleContent.trim()) {
                    alert('请先上传字幕文件或粘贴字幕内容');
                    return;
                  }

                  try {
                    // 尝试解析字幕内容
                    let parsedSubtitles = [];
                    
                    // 尝试解析 SRT
                    parsedSubtitles = parseSRT(newSubtitleContent);
                    
                    // 如果 SRT 解析失败，尝试 VTT
                    if (parsedSubtitles.length === 0) {
                      parsedSubtitles = parseVTT(newSubtitleContent);
                    }

                    if (parsedSubtitles.length === 0) {
                      alert('无法解析字幕内容，请检查格式是否正确');
                      return;
                    }

                    // 确认替换
                    if (window.confirm(`确认要替换字幕吗？将替换 ${parsedSubtitles.length} 条字幕，此操作不可恢复！`)) {
                      // 更新字幕
                      setSubtitles(parsedSubtitles);
                      setTempSubtitles(parsedSubtitles);
                      setOriginalSubtitles(parsedSubtitles);
                      
                      // 保存到服务器
                      if (editingSubtitleFile && editingSubtitleFile.id) {
                        try {
                          const response = await fetch('/api/subtitles/save', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              videoId: editingSubtitleFile.id,
                              subtitles: parsedSubtitles
                            })
                          });
                          
                          const data = await response.json();
                          if (data.success) {
                            console.log('字幕已保存到服务器:', data.subtitleUrl);
                          } else {
                            alert('字幕保存到服务器失败: ' + (data.error || '未知错误'));
                          }
                        } catch (error) {
                          console.error('保存字幕失败:', error);
                          alert('字幕保存到服务器失败，请重试');
                        }
                      }
                      
                      // 关闭模态框
                      setShowReplaceSubtitleModal(false);
                      setNewSubtitleContent('');
                      setNewSubtitleFile(null);
                      
                      alert(`成功替换 ${parsedSubtitles.length} 条字幕！`);
                    }
                  } catch (error) {
                    console.error('解析字幕失败:', error);
                    alert('解析字幕失败，请检查格式是否正确');
                  }
                }}
                style={{
                  background: '#ffc107',
                  border: 'none',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e0a800'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffc107'}
              >
                确认替换
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminManage;